import {
	EditIntegrationParams,
	VAPARLoginParams,
} from './../../types/integration.types';
import { error, success } from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';

import { navigateTo } from '@Actions/navigate.action';
import { addRouteNamespace } from '@Utils/actions';
import { put, retry, takeLatest } from 'redux-saga/effects';

import {
	CreateIntegrationParams,
	Integration,
	ListIntegrationsParams,
} from '../../types/integration.types';
import * as integrationActions from '../actions/integrations.actions';
import {
	createIntegrationService,
	deleteEsriIntegrationService,
	getIntegrationsService,
	getIntegrationService,
	updateEsriIntegrationService,
	VAPARLoginService,
} from '../services/integrations.service';
import { ns } from '@Translations/translations';

function* getIntegrationsSaga(action: PayloadAction<ListIntegrationsParams>) {
	const { getIntegrationsResolved, getIntegrationsRejected } =
		integrationActions;

	try {
		const response: AxiosResponse<Integration[]> = yield retry(
			3,
			1500,
			getIntegrationsService,
			action.payload,
		);
		if (response.data) {
			yield put(getIntegrationsResolved(response.data));
		} else {
			yield put(getIntegrationsRejected());
			yield put(error(t('Failed to get integration', { ns })));
		}
	} catch (e) {
		yield put(getIntegrationsRejected());
		yield put(error(t('Failed to get integration', { ns })));
	}
}
function* getIntegrationSaga(action: PayloadAction<string>) {
	const {
		getSingleIntegrationByIdResolved,
		getSingleIntegrationByIdRejected,
	} = integrationActions;

	try {
		const response: AxiosResponse<EditIntegrationParams> = yield retry(
			3,
			1500,
			getIntegrationService,
			action.payload,
		);
		if (response.data) {
			yield put(getSingleIntegrationByIdResolved(response.data));
		} else {
			yield put(getSingleIntegrationByIdRejected());
			yield put(error(t('Failed to get integration', { ns })));
		}
	} catch (e) {
		yield put(getSingleIntegrationByIdRejected());
		yield put(error(t('Failed to get integration', { ns })));
	}
}
function* updateEsriIntegrationSaga(
	action: PayloadAction<EditIntegrationParams>,
) {
	const { updateIntegrationResolved, updateIntegrationRejected } =
		integrationActions;

	try {
		const response: AxiosResponse<EditIntegrationParams> = yield retry(
			3,
			1500,
			updateEsriIntegrationService,
			action.payload,
		);
		if (response.data) {
			yield put(updateIntegrationResolved());
			yield put(success('integration updated successfully.'));
			yield put(navigateTo(addRouteNamespace('integrations')));
		} else {
			yield put(updateIntegrationRejected());
			yield put(error(t('Failed to update integration', { ns })));
		}
	} catch (e) {
		yield put(updateIntegrationRejected());
		yield put(error(t('Failed to update integration', { ns })));
	}
}

function* createIntegrationSaga(
	action: PayloadAction<CreateIntegrationParams>,
) {
	// portal is needed to exchange refresh token. Default to a valid value if not present or empty.
	if (!action.payload.portal || action.payload.portal == '') {
		action.payload.portal = 'https://www.arcgis.com/sharing/rest';
	}
	const { createIntegrationResolved, createIntegrationRejected } =
		integrationActions;

	try {
		const response: AxiosResponse<Integration> = yield retry(
			3,
			1500,
			createIntegrationService,
			action.payload,
		);
		if (response?.data?._id && response.status < 300) {
			yield put(createIntegrationResolved(response.data));
			yield put(success(t('Integration created', { ns })));

			yield put(navigateTo(addRouteNamespace('integrations')));
		} else {
			yield put(createIntegrationRejected());
			if (response?.status >= 300) {
				yield put(error(t('Failed to create integration', { ns })));
			}
			if (!response?.data?._id) {
				yield put(
					error(t('Integration for tenant already exists', { ns })),
				);
			}
		}
	} catch (e) {
		console.error(e);
		yield put(createIntegrationRejected());
		yield put(error(t('Failed to create integration', { ns })));
	}
}

function* deleteIntegrationSaga(action: PayloadAction<string>) {
	const { deleteIntegrationResolved, deleteIntegrationRejected } =
		integrationActions;
	try {
		const response: AxiosResponse<null> = yield retry(
			3,
			1500,
			deleteEsriIntegrationService,
			action.payload,
		);
		if (response.status < 300) {
			yield put(deleteIntegrationResolved());
			yield put(success(t('Integration deleted', { ns })));
			yield put(integrationActions.getIntegrations({}));
			yield put(navigateTo(addRouteNamespace('integrations')));
		} else {
			yield put(deleteIntegrationRejected());
			yield put(error(t('Failed to delete integration', { ns })));
		}
	} catch (e) {
		yield put(deleteIntegrationRejected());
		yield put(error(t('Failed to delete integration', { ns })));
	}
}

function* VAPARLoginSaga(action: PayloadAction<VAPARLoginParams>) {
	try {
		const response: AxiosResponse = yield retry(
			3,
			1500,
			VAPARLoginService,
			action.payload,
		);

		return response.data;
	} catch (e) {
		yield put(error(t('Failed to log into VAPAR', { ns })));
	}
}

function* watchGetIntegrations() {
	yield takeLatest(integrationActions.getIntegrations, getIntegrationsSaga);
}
function* watchGetIntegration() {
	yield takeLatest(
		integrationActions.getSingleIntegrationById,
		getIntegrationSaga,
	);
}
function* watchUpdateEsriIntegration() {
	yield takeLatest(
		integrationActions.updateIntegration,
		updateEsriIntegrationSaga,
	);
}

function* watchCreateIntegration() {
	yield takeLatest(
		integrationActions.createIntegration,
		createIntegrationSaga,
	);
}
function* watchDeleteIntegration() {
	yield takeLatest(
		integrationActions.deleteIntegration,
		deleteIntegrationSaga,
	);
}

function* watchVAPARLogin() {
	yield takeLatest(integrationActions.vaparLogin, VAPARLoginSaga);
}

export default [
	watchGetIntegrations(),
	watchCreateIntegration(),
	watchDeleteIntegration(),
	watchGetIntegration(),
	watchUpdateEsriIntegration(),
	watchVAPARLogin(),
];
